import { useCallback, useRef, useState } from "react";
import { Camera, CheckCircle, Upload } from "lucide-react";
import { prepareBillFile } from "@/utils/billFileValidation";

interface BillUploadDropzoneProps {
  onFileSelected: (file: File) => void;
  selectedFile?: File | null;
  variant?: "compact" | "full";
  /** Visual tone — light for cream homepage, dark for quote flow */
  tone?: "dark" | "light";
}

const FILE_ACCEPT = "application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png";

export default function BillUploadDropzone({
  onFileSelected,
  selectedFile = null,
  variant = "full",
  tone = "dark",
}: BillUploadDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const isLight = tone === "light";

  const handleFile = useCallback(
    (file: File) => {
      const { file: prepared, error: validationError } = prepareBillFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setError(null);
      onFileSelected(prepared);
    },
    [onFileSelected],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const f = e.dataTransfer.files[0];
      if (f) handleFile(f);
    },
    [handleFile],
  );

  const openFilePicker = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    inputRef.current?.click();
  }, []);

  const openCamera = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    cameraInputRef.current?.click();
  }, []);

  const isCompact = variant === "compact";

  const idleBorder = isLight
    ? isCompact
      ? "border-[rgba(17,17,17,0.2)] bg-white hover:border-[#D4FF00]/60 hover:bg-[#D4FF00]/5"
      : "border-[rgba(17,17,17,0.2)] bg-[var(--home-bg)] hover:border-[#D4FF00]/40 hover:bg-[#D4FF00]/5"
    : isCompact
      ? "border-white/20 bg-white/5 hover:border-[#D4FF00]/60 hover:bg-[#D4FF00]/5"
      : "border-white/20 bg-[#0d1b2e] hover:border-[#D4FF00]/40 hover:bg-[#D4FF00]/5";

  const titleClass = isLight
    ? isCompact
      ? "text-base text-[#111111]"
      : "text-lg text-[#111111]"
    : isCompact
      ? "text-base text-white"
      : "text-lg";

  const subtitleClass = isLight ? "text-sm text-[#666666]" : "text-sm text-white/40";

  const cameraBtnClass = isLight
    ? "border-[rgba(17,17,17,0.12)] bg-white text-[#333333] hover:border-[#D4FF00]/50 hover:bg-[#D4FF00]/10 hover:text-[#111111]"
    : "border-white/10 bg-white/5 text-white/80 hover:border-[#D4FF00]/40 hover:bg-[#D4FF00]/10 hover:text-white";

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={openFilePicker}
        className={`relative cursor-pointer border-2 border-solid text-center transition-all duration-200 ${
          isCompact ? "rounded-xl p-8" : "rounded-2xl p-16"
        } ${
          dragging
            ? "border-[#D4FF00] bg-[#D4FF00]/10"
            : selectedFile
              ? "border-[#D4FF00]/60 bg-[#D4FF00]/5"
              : idleBorder
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={FILE_ACCEPT}
          className="hidden"
          onChange={onInputChange}
        />
        {selectedFile ? (
          isCompact ? (
            <div className="flex items-center justify-center gap-3">
              <CheckCircle className="h-6 w-6 text-[#0E9E77]" />
              <span className="text-[#0E9E77] font-medium">{selectedFile.name}</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <CheckCircle className="h-12 w-12 text-[#D4FF00]" />
              <p className="text-[#D4FF00] font-semibold">{selectedFile.name}</p>
              <p className={isLight ? "text-sm text-[#666666]" : "text-sm text-white/40"}>
                Click to change file
              </p>
            </div>
          )
        ) : (
          <div className={isCompact ? "" : "flex flex-col items-center gap-4"}>
            <div
              className={`mx-auto flex items-center justify-center bg-[#D4FF00]/10 border border-[#D4FF00]/30 ${
                isCompact ? "mb-4 h-14 w-14 rounded-full" : "h-16 w-16 rounded-2xl"
              }`}
            >
              <Upload className={isCompact ? "h-6 w-6 text-[#0E9E77]" : "h-7 w-7 text-[#D4FF00]"} />
            </div>
            <div>
              <p className={`font-medium mb-1 ${titleClass}`}>
                {isCompact ? "Upload your energy bill" : "Drop your bill here"}
              </p>
              <p className={subtitleClass}>
                {isCompact
                  ? "PDF, JPG or PNG · drag & drop or click to upload"
                  : "or click to browse · PDF, JPG, PNG"}
              </p>
            </div>
          </div>
        )}
      </div>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onInputChange}
      />

      <button
        type="button"
        onClick={openCamera}
        className={`flex w-full items-center justify-center gap-2 rounded-xl border transition-colors ${cameraBtnClass} ${
          isCompact ? "px-4 py-3 text-sm" : "px-5 py-3.5 text-base"
        }`}
      >
        <Camera className={isCompact ? "h-4 w-4 text-[#0E9E77]" : "h-5 w-5 text-[#D4FF00]"} />
        Take photo of bill
      </button>

      {error && (
        <p className="text-sm text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
